import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { Card, CardContent, CardHeader, CardTitle } from "@beweegbaas/ui";
import { m } from "#/paraglide/messages";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { LayoutAuth } from "#/layouts/layout.auth";
import { resetPasswordFn } from "#/modules/util.auth/auth.functions";
import { resetPasswordSchema } from "#/modules/generic.login/schema.reset-password";
import { useAppForm } from "#/modules/util.forms/form";
import { z } from "zod/v4";

export const Route = createFileRoute("/$locale/{-$schoolyear}/reset-password")({
  validateSearch: z.object({
    email: z.string().catch(""),
    token: z.string().catch(""),
  }),
  beforeLoad: ({ search, params }) => {
    if (!search.email || !search.token) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/login",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
  },
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { email, token } = Route.useSearch();
  const navigate = useNavigate();
  const resetPassword = useServerFn(resetPasswordFn);

  const form = useAppForm({
    defaultValues: { password: "" },
    validators: { onSubmit: resetPasswordSchema },
    onSubmit: async ({ value }) => {
      try {
        const res = await resetPassword({
          data: { email, token, newPassword: value.password },
        });
        if (res) {
          form.setErrorMap({ onSubmit: { form: res.formError, fields: {} } });
          return;
        }
        void navigate({
          from: Route.fullPath,
          to: "/$locale/{-$schoolyear}/login",
          params: (prev) => prev,
        });
      } catch (error) {
        captureUnexpectedError(error, "reset-password");
        form.setErrorMap({
          onSubmit: { form: m["common.something_went_wrong_email"](), fields: {} },
        });
      }
    },
  });

  return (
    <LayoutAuth showVisuals={false}>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{m["auth.reset_password.page_title"]()}</CardTitle>
        </CardHeader>
        <CardContent>
          <form.AppForm>
            <form.FormRoot className="flex flex-col gap-4">
              <form.AppField
                name="password"
                children={(field) => (
                  <field.PasswordTextField label={m["account.new_password"]()} showRequirements />
                )}
              />
              <form.SubmitButton className="w-full">
                {m["auth.reset_password.submit_button"]()}
              </form.SubmitButton>
            </form.FormRoot>
          </form.AppForm>
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
